import { z } from "zod";

// ---------------------------------------------------------------------------
// Auth schemas
//
// Phase 1 (platform-base) scope: register/login/refresh only. Catalog,
// draft, admin, migrate, and feedback schemas are added back in as later
// phases extract their own slices.
// ---------------------------------------------------------------------------

export const emailSchema = z.string().email().max(255);
export const passwordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters")
  .max(128)
  .regex(/[a-z]/, "Password must contain a lowercase letter")
  .regex(/[A-Z]/, "Password must contain an uppercase letter")
  .regex(/[0-9]/, "Password must contain a number");

export const registerSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  name: z.string().min(1).max(120),
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const forgotPasswordSchema = z.object({
  email: emailSchema,
});
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z.object({
  token: z.string().min(1),
  newPassword: passwordSchema,
});
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

export const verifyEmailSchema = z.object({
  token: z.string().min(1),
});
export type VerifyEmailInput = z.infer<typeof verifyEmailSchema>;

export const resendVerificationSchema = z.object({
  email: emailSchema,
});
export type ResendVerificationInput = z.infer<typeof resendVerificationSchema>;

export const refreshSchema = z.object({
  refreshToken: z.string().min(1),
});
export type RefreshInput = z.infer<typeof refreshSchema>;

export const googleAuthSchema = z.object({
  idToken: z.string().min(1),
});
export type GoogleAuthInput = z.infer<typeof googleAuthSchema>;

// ---------------------------------------------------------------------------
// Generic param schemas
// ---------------------------------------------------------------------------

export const idParamSchema = z.object({
  id: z.string().uuid(),
});

// ---------------------------------------------------------------------------
// Enterprise schemas
// ---------------------------------------------------------------------------

export const inviteUserSchema = z.object({
  firstName: z.string().min(1, "First name is required").max(80),
  lastName: z.string().min(1, "Last name is required").max(80),
  email: emailSchema,
  jobTitle: z.string().max(120).optional(),
  appKeys: z.array(z.string()).min(1, "Select at least one app"),
  roleIds: z.array(z.string().uuid()).min(1, "Select at least one role"),
});
export type InviteUserInput = z.infer<typeof inviteUserSchema>;

export const approveUserSchema = z.object({ userId: z.string().uuid() });
export type ApproveUserInput = z.infer<typeof approveUserSchema>;

export const updateEnterpriseSettingsSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  notifications: z.object({
    userRequests: z.boolean(),
    migration: z.boolean(),
    roles: z.boolean(),
    digest: z.boolean(),
  }).optional(),
});
export type UpdateEnterpriseSettingsInput = z.infer<typeof updateEnterpriseSettingsSchema>;

export const toggleModuleSchema = z.object({
  moduleId: z.string().uuid(),
  enabled: z.boolean(),
});
export type ToggleModuleInput = z.infer<typeof toggleModuleSchema>;

// ---------------------------------------------------------------------------
// Profile schemas
// ---------------------------------------------------------------------------

export const updateProfileSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  jobTitle: z.string().max(120).optional(),
  timezone: z.string().max(60).optional(),
  dateFormat: z.string().max(20).optional(),
});
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: passwordSchema,
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

// ---------------------------------------------------------------------------
// Platform schemas
// ---------------------------------------------------------------------------

export const createEnterpriseSchema = z.object({
  name: z.string().min(1).max(120),
  slug: z.string().min(2).max(40).regex(/^[a-z0-9-]+$/, "Slug must be lowercase letters, numbers and hyphens only"),
  adminEmail: emailSchema,
  moduleKeys: z.array(z.string()).min(1, "Select at least one module"),
});
export type CreateEnterpriseInput = z.infer<typeof createEnterpriseSchema>;

export const resetPasswordAdminSchema = z.object({ userId: z.string().uuid() });

export const suspendEnterpriseSchema = z.object({ enterpriseId: z.string().uuid() });

export const updateEmailSettingsSchema = z.object({
  provider: z.enum(["resend"]).optional(),
  // Omit or empty string = keep the existing key unchanged. Only a
  // genuinely new, non-empty value ever triggers re-encrypting and
  // storing a new key — this endpoint's own PATCH semantics, not a
  // schema-level requirement, since "leave the secret alone" has to
  // stay possible on every other field update.
  apiKey: z.string().max(500).optional(),
  fromEmail: z.string().email().max(255).optional(),
  fromName: z.string().max(120).optional(),
  enabled: z.boolean().optional(),
  monthlyLimit: z.number().int().positive().nullable().optional(),
});
export type UpdateEmailSettingsInput = z.infer<typeof updateEmailSettingsSchema>;

// ---------------------------------------------------------------------------
// Feedback schemas
// ---------------------------------------------------------------------------

export const createFeedbackSchema = z.object({
  type: z.enum(["feedback", "bug"]),
  title: z.string().min(1).max(200),
  description: z.string().min(1).max(5000),
  draftId: z.string().uuid().optional(),
});
export type CreateFeedbackInput = z.infer<typeof createFeedbackSchema>;

export const updateFeedbackStatusSchema = z.object({
  status: z.enum(["open", "under_review", "resolved", "closed"]),
  reviewerNote: z.string().max(2000).optional(),
});
export type UpdateFeedbackStatusInput = z.infer<typeof updateFeedbackStatusSchema>;

// ---------------------------------------------------------------------------
// File storage
// ---------------------------------------------------------------------------

export const uploadFileSchema = z.object({
  fileName: z.string().min(1).max(255),
  mimeType: z.string().min(1).max(120),
  // Base64-encoded file bytes, same convention as ConfigIQ's own
  // hcm_file_templates.templateBody for docx/xlsx uploads — avoids a
  // multipart/multer dependency for this one feature.
  contentBase64: z.string().min(1),
  folderPath: z.string().max(255).optional(),
});
export type UploadFileInput = z.infer<typeof uploadFileSchema>;

export const updateStorageSettingsSchema = z.object({
  defaultQuotaBytes: z.number().int().positive(),
});
export type UpdateStorageSettingsInput = z.infer<typeof updateStorageSettingsSchema>;

// ---------------------------------------------------------------------------
// Scheduler
// ---------------------------------------------------------------------------

const triggerConfigSchema = z.union([
  z.object({ expression: z.string().min(1) }),          // cron
  z.object({ seconds: z.number().int().positive() }),    // interval
  z.object({ at: z.string().datetime() }),               // one_time
]);

export const createScheduledJobSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  jobType: z.enum(["etl", "report_generation"]),
  // Omit or null = platform-wide (platform admin only — enforced in the
  // service layer, not here, since it needs the caller's own role).
  enterpriseId: z.string().uuid().nullable().optional(),
  triggerType: z.enum(["cron", "interval", "one_time"]),
  triggerConfig: triggerConfigSchema,
  webhookUrl: z.string().url(),
  jobConfig: z.record(z.string(), z.unknown()),
  notificationType: z.enum(["email", "bell", "both", "none"]).default("bell"),
});
export type CreateScheduledJobInput = z.infer<typeof createScheduledJobSchema>;

export const updateScheduledJobSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(2000).nullable().optional(),
  triggerType: z.enum(["cron", "interval", "one_time"]).optional(),
  triggerConfig: triggerConfigSchema.optional(),
  webhookUrl: z.string().url().optional(),
  jobConfig: z.record(z.string(), z.unknown()).optional(),
  notificationType: z.enum(["email", "bell", "both", "none"]).optional(),
  enabled: z.boolean().optional(),
});
export type UpdateScheduledJobInput = z.infer<typeof updateScheduledJobSchema>;

export const jobRunCallbackSchema = z.object({
  status: z.enum(["success", "failed"]),
  resultMessage: z.string().max(5000).optional(),
});
export type JobRunCallbackInput = z.infer<typeof jobRunCallbackSchema>;
